import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_EXTERNAL_COMPANIES } from '../../../../gql/queries/actions/externalCompanies';

// Utils
import { AuthContextType } from '../../../../hooks/useAuth';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';

// Constants
import { formExternalCompanyManagementNaming } from '../../../../components/forms/AddExternalCompanyForm/constants';

// Types
import { ExternalCompanyRow } from '../../../../components/display/tables/formTables/managementTables/ExternalCompaniesManagementTable/types';

const loadCompanies = async (
  userData: AuthContextType | null
): Promise<ExternalCompanyRow[]> => {
  try {
    if (!userData?.user) return [];

    const { data } = await client.query({
      query: FETCH_EXTERNAL_COMPANIES,
      // network-only: esta pantalla ES la fuente de verdad del catálogo, no
      // puede servir una copia de caché tras crear/desactivar.
      fetchPolicy: 'network-only',
    });

    return [...(data?.externalCompanies ?? [])]
      .map(
        (company): ExternalCompanyRow => ({
          id: company.id,
          companyName: company.companyName,
          isActive: company.isActive,
          externalWorksCount: company.externalWorksCount ?? 0,
        })
      )
      .sort((a, b) => a.companyName.localeCompare(b.companyName));
  } catch (err) {
    Sentry.captureException(err);
    return [];
  }
};

export const ExternalCompaniesManagementLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  try {
    const companies = await loadCompanies(userData);

    const rawCache = sessionStorage.getItem(
      formExternalCompanyManagementNaming.NEW_EXTERNAL_COMPANY_MANAGEMENT_FORM
    );
    const cacheData = rawCache
      ? JSON.parse(decompressSessionStorage(rawCache))
      : {};

    return {
        activeCompanies: companies.filter((company) => company.isActive),
        inactiveCompanies: companies.filter((company) => !company.isActive),
        cacheData,
      };
  } catch (error) {
    Sentry.captureException(error);
    return { activeCompanies: [], inactiveCompanies: [], cacheData: {} };
  }
};

  return { data: load() };
};
